import { HttpError, type Schemas, type UserListItem, api } from '@/api/client'
import { EmptyState } from '@/components/shared/EmptyState'
import { FormField } from '@/components/shared/FormField'
import { PageHeader } from '@/components/shared/PageHeader'
import { UserAvatar } from '@/components/shared/UserAvatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { Dialog } from '@/components/ui/dialog'
import { Dropdown } from '@/components/ui/dropdown'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { hasPermission } from '@/hooks/usePermission'
import { writeClipboard } from '@/lib/clipboard'
import { cn } from '@/lib/cn'
import { formatRelativeDate } from '@/lib/formatters'
import { useAuthStore } from '@/stores/auth.store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, redirect } from '@tanstack/react-router'
import { MoreHorizontal, Plus, Users as UsersIcon } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Route as settingsLayoutRoute } from '../layout'

type UserStatus = 'active' | 'invited' | 'suspended'

const SYSTEM_ROLE_NAMES = ['admin', 'manager', 'agent', 'driver', 'client'] as const
type SystemRoleName = (typeof SYSTEM_ROLE_NAMES)[number]

function UserStatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation('settings')
  const variant = status === 'active' ? 'success' : status === 'invited' ? 'info' : 'warning'
  return (
    <Badge variant={variant} withDot>
      {t(`users.status.${status}`)}
    </Badge>
  )
}

const inviteSchema = z.object({
  first_name: z.string().min(1).max(50),
  last_name: z.string().min(1).max(50),
  email: z.string().email(),
  role: z.enum(SYSTEM_ROLE_NAMES),
})

type InviteForm = z.infer<typeof inviteSchema>

function InviteUserDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const toast = useToast()

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { first_name: '', last_name: '', email: '', role: 'agent' },
  })

  const mutation = useMutation({
    mutationFn: (body: InviteForm) =>
      api.users.invite({
        email: body.email,
        first_name: body.first_name,
        last_name: body.last_name,
        role_names: [body.role],
      } as Schemas['InviteUserRequest']),
    onSuccess: (user) => {
      toast.show({
        title: t('users.invite_dialog.sent', { email: user.email }),
        variant: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      reset()
      onClose()
    },
    onError: (error) => {
      const message = error instanceof HttpError ? error.message : tCommon('error_generic')
      toast.show({
        title: t('users.invite_dialog.failed'),
        description: message,
        variant: 'error',
      })
    },
  })

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!mutation.isPending) {
          reset()
          onClose()
        }
      }}
      title={t('users.invite_dialog.title')}
      description={t('users.invite_dialog.subtitle')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={handleSubmit((data) => mutation.mutate(data))}
            loading={mutation.isPending}
          >
            {t('users.invite_dialog.submit')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          label={t('users.invite_dialog.first_name')}
          htmlFor="invite-first"
          required
          error={errors.first_name ? t('users.invite_dialog.required') : undefined}
        >
          <Input id="invite-first" {...register('first_name')} />
        </FormField>
        <FormField
          label={t('users.invite_dialog.last_name')}
          htmlFor="invite-last"
          required
          error={errors.last_name ? t('users.invite_dialog.required') : undefined}
        >
          <Input id="invite-last" {...register('last_name')} />
        </FormField>
        <FormField
          label={t('users.invite_dialog.email')}
          htmlFor="invite-email"
          required
          className="md:col-span-2"
          error={errors.email ? t('users.invite_dialog.invalid_email') : undefined}
        >
          <Input id="invite-email" type="email" {...register('email')} />
        </FormField>
        <FormField
          label={t('users.invite_dialog.role')}
          htmlFor="invite-role"
          required
          className="md:col-span-2"
        >
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <Select
                id="invite-role"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              >
                {SYSTEM_ROLE_NAMES.map((r) => (
                  <option key={r} value={r}>
                    {t(`users.system_roles.${r}`)}
                  </option>
                ))}
              </Select>
            )}
          />
        </FormField>
      </div>
    </Dialog>
  )
}

function EditRolesDialog({
  user,
  open,
  onClose,
}: {
  user: UserListItem | null
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const toast = useToast()
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  const currentRoles = user?.roles ?? []

  function ensureInit() {
    if (user && Object.keys(selected).length === 0 && currentRoles.length > 0) {
      setSelected(Object.fromEntries(currentRoles.map((r) => [r.id, true])))
    }
  }
  ensureInit()

  const mutation = useMutation({
    mutationFn: (roleIds: string[]) =>
      user ? api.users.setRoles(user.id, { role_ids: roleIds }) : Promise.resolve(user),
    onSuccess: () => {
      toast.show({ title: t('users.roles_dialog.saved'), variant: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      onClose()
      setSelected({})
    },
    onError: (error) => {
      const message = error instanceof HttpError ? error.message : tCommon('error_generic')
      toast.show({
        title: t('users.roles_dialog.save_failed'),
        description: message,
        variant: 'error',
      })
    },
  })

  if (!user) return null

  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!mutation.isPending) {
          setSelected({})
          onClose()
        }
      }}
      title={t('users.roles_dialog.title', { name: displayName })}
      description={t('users.roles_dialog.subtitle')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={() => {
              const ids = Object.entries(selected)
                .filter(([, v]) => v)
                .map(([k]) => k)
              mutation.mutate(ids)
            }}
            loading={mutation.isPending}
          >
            {tCommon('save')}
          </Button>
        </>
      }
    >
      <ul className="divide-y divide-border">
        {currentRoles.map((role) => {
          const knownName = (SYSTEM_ROLE_NAMES as readonly string[]).includes(role.name)
            ? (role.name as SystemRoleName)
            : null
          return (
            <li key={role.id} className="flex items-start justify-between gap-3 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  {knownName ? t(`users.system_roles.${knownName}`) : role.name}
                </p>
                {knownName ? (
                  <p className="text-xs text-muted-foreground">
                    {t(`users.system_roles.${knownName}_desc`)}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={selected[role.id] ?? true}
                onClick={() => setSelected((s) => ({ ...s, [role.id]: !(s[role.id] ?? true) }))}
                className={cn(
                  'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
                  (selected[role.id] ?? true) ? 'bg-primary' : 'bg-muted',
                )}
              >
                <span
                  className={cn(
                    'inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform',
                    (selected[role.id] ?? true) ? 'translate-x-5' : 'translate-x-0.5',
                  )}
                />
              </button>
            </li>
          )
        })}
      </ul>
    </Dialog>
  )
}

function UsersPage() {
  const { t, i18n } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')
  const locale = (i18n.language.startsWith('fr') ? 'fr' : 'en') as 'fr' | 'en'
  const perms = useAuthStore((s) => s.permissions)
  const canCreate = hasPermission(perms, 'users:create')
  const canUpdate = hasPermission(perms, 'users:update')
  const canDelete = hasPermission(perms, 'users:delete')
  const canManageRoles = hasPermission(perms, 'roles:manage')
  const toast = useToast()
  const queryClient = useQueryClient()

  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [rolesUser, setRolesUser] = useState<UserListItem | null>(null)
  const [deleteUser, setDeleteUser] = useState<UserListItem | null>(null)

  const query = useQuery({
    queryKey: ['users', { status: statusFilter || undefined }],
    queryFn: () =>
      api.users.list(statusFilter ? { status: statusFilter as UserStatus } : undefined),
  })

  const patchMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) =>
      api.users.patch(id, { status } as Schemas['UpdateUserRequest']),
    onSuccess: (_, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.show({
        title:
          vars.status === 'suspended' ? t('users.suspended_toast') : t('users.unsuspended_toast'),
        variant: 'success',
      })
    },
    onError: () => {
      toast.show({ title: t('users.action_failed'), variant: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.users.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.show({ title: t('users.deleted_toast'), variant: 'success' })
      setDeleteUser(null)
    },
    onError: () => {
      toast.show({ title: t('users.action_failed'), variant: 'error' })
      setDeleteUser(null)
    },
  })

  // copyInviteLink re-issues a fresh invite token + writes the URL
  // straight into the clipboard. Works on both desktop and mobile
  // Safari; falls back to opening a prompt when the Clipboard API is
  // unavailable (e.g. very old browsers / non-HTTPS dev origins).
  const copyInviteLink = async (u: UserListItem) => {
    try {
      const res = await api.users.inviteLink(u.id)
      await writeClipboard(res.url)
      toast.show({
        title: t('users.invite_link_copied'),
        description: res.url,
        variant: 'success',
      })
    } catch (err) {
      toast.show({
        title: t('users.action_failed'),
        description: err instanceof HttpError ? err.message : undefined,
        variant: 'error',
      })
    }
  }

  const columns: DataTableColumn<UserListItem>[] = [
    {
      key: 'name',
      header: t('users.col.name'),
      cell: (u) => (
        <div className="flex items-center gap-3">
          <UserAvatar user={u} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">
              {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
            </p>
            <p className="truncate text-xs text-muted-foreground">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'roles',
      header: t('users.col.status'),
      cell: (u) => <UserStatusBadge status={u.status as UserStatus} />,
    },
    {
      key: 'last_login',
      header: t('users.col.last_login'),
      cell: (u) =>
        u.last_login_at ? (
          <span className="text-sm text-muted-foreground">
            {formatRelativeDate(u.last_login_at, locale)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t('users.never_logged_in')}</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (u) => {
        const items: Array<{
          label: string
          onSelect: () => void
          disabled?: boolean
          destructive?: boolean
        }> = []
        if (canCreate && u.status === 'invited') {
          items.push({
            label: t('users.actions.copy_invite_link'),
            onSelect: () => copyInviteLink(u),
          })
        }
        if (canManageRoles && (u.roles?.length ?? 0) > 0) {
          items.push({
            label: t('users.actions.edit_roles'),
            onSelect: () => setRolesUser(u),
          })
        }
        if (canUpdate) {
          if (u.status === 'active') {
            items.push({
              label: t('users.actions.suspend'),
              onSelect: () => patchMutation.mutate({ id: u.id, status: 'suspended' }),
            })
          } else if (u.status === 'suspended') {
            items.push({
              label: t('users.actions.unsuspend'),
              onSelect: () => patchMutation.mutate({ id: u.id, status: 'active' }),
            })
          }
        }
        if (canDelete) {
          items.push({
            label: t('users.actions.delete'),
            onSelect: () => setDeleteUser(u),
            destructive: true,
          })
        }
        if (items.length === 0) return null
        return (
          <Dropdown
            align="end"
            trigger={
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-transparent text-muted-foreground transition-all hover:-translate-y-0.5 hover:border-border hover:bg-muted hover:text-foreground hover:shadow-sm">
                <MoreHorizontal className="h-4 w-4" />
              </span>
            }
            items={items}
          />
        )
      },
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('users.title')}
        description={t('users.subtitle')}
        actions={
          canCreate ? (
            <Button onClick={() => setInviteOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('users.invite')}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              className="md:w-60"
              value={statusFilter}
              onChange={(e) => setStatusFilter((e.target.value as UserStatus) || '')}
            >
              <option value="">{t('users.filter_status_all')}</option>
              <option value="active">{t('users.status.active')}</option>
              <option value="invited">{t('users.status.invited')}</option>
              <option value="suspended">{t('users.status.suspended')}</option>
            </Select>
            {query.data ? (
              <span className="text-xs text-muted-foreground">
                {t('users.total_count', {
                  count: query.data.data.length,
                  defaultValue: `${query.data.data.length} utilisateur(s)`,
                })}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.data && query.data.data.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title={t('users.empty_title')}
          description={t('users.empty_description')}
          action={
            canCreate ? (
              <Button onClick={() => setInviteOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('users.invite')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={query.data?.data ?? []}
          rowKey={(u) => u.id}
          emptyMessage={t('users.empty_title')}
        />
      )}

      <InviteUserDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />

      <EditRolesDialog
        user={rolesUser}
        open={rolesUser !== null}
        onClose={() => setRolesUser(null)}
      />

      <ConfirmDialog
        open={deleteUser !== null}
        onClose={() => setDeleteUser(null)}
        onConfirm={() => deleteUser && deleteMutation.mutate(deleteUser.id)}
        title={t('users.delete_confirm.title')}
        message={
          deleteUser
            ? t('users.delete_confirm.message', {
                name:
                  [deleteUser.first_name, deleteUser.last_name].filter(Boolean).join(' ') ||
                  deleteUser.email,
              })
            : undefined
        }
        confirmLabel={tCommon('delete')}
        loading={deleteMutation.isPending}
        destructive
      />
    </div>
  )
}

export const Route = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/users',
  beforeLoad: () => {
    const perms = useAuthStore.getState().permissions
    if (!hasPermission(perms, 'users:read')) {
      throw redirect({ to: '/settings/profile' })
    }
  },
  component: UsersPage,
})
